<template>
  <div class="sign-in-box">
    <validation-observer
      ref="observer"
      v-slot="{ invalid }"
      tag="form"
      @submit.prevent="validateForm()">
      <validation-provider name="email" rules="required|email" slim>
        <template slot-scope="{ errors }">
          <oie-base-input
            id="sign-in-username"
            v-model="username"
            :label-text="`${tStatic('loginEmail')}*`"
            :placeholder="tStatic('loginEmailPlaceholder')"
            :error-messages="errors"></oie-base-input>
        </template>
      </validation-provider>

      <validation-provider name="password" rules="required" slim>
        <template slot-scope="{ errors }">
          <oie-base-input
            id="sign-in-password"
            v-model="password"
            type="password"
            :label-text="`${tStatic('password')}*`"
            :placeholder="tStatic('passwordPlaceholder')"
            :error-messages="errors"></oie-base-input>
        </template>
      </validation-provider>

      <div class="sign-in-box__actions">
        <oie-base-button
          v-element-loader="{ endpoint: 'auth/signin' }"
          button-type="button"
          :text="tStatic('signIn')"
          css-classes="button--with-border button--blue sign-in-box__button"
          type="submit"></oie-base-button>

        <oie-base-checkbox
          v-model="rememberMe"
          is-for="stay-signed-in"
          :text="tStatic('rememberMe')"
          size="large"
          css-classes="sign-in-box__checkbox"></oie-base-checkbox>
      </div>
    </validation-observer>

    <a v-if="clientModel.forgotPasswordUrl" :href="clientModel.forgotPasswordUrl" class="sign-in-box__reset-password">
      {{ tStatic('forgotPassword') }}
    </a>
  </div>
</template>

<script>
import httpService from '@/core/http-service'
import cartMerge from '@/mixins/cart-merge.js'

export default {
  mixins: [cartMerge],
  props: {
    enableMergeCart: {
      type: Boolean,
      default: true
    },
    fetchInvoiceData: {
      type: Boolean,
      default: false
    },
    setGtmEvent: {
      type: Function,
      default: undefined
    },
    returnUrl: {
      type: String,
      default: undefined
    }
  },
  data: () => ({
    username: null,
    password: null,
    rememberMe: null
  }),
  methods: {
    async validateForm() {
      const isValid = await this.$refs.observer.validate()

      if (isValid) {
        this.signIn().then(() => {
          if (this.setGtmEvent) {
            this.setGtmEvent()
          }
        })
      }
    },
    async signIn() {
      httpService.post('auth/signin', {
        username: this.username,
        password: this.password,
        rememberMe: this.rememberMe,
        fetchInvoiceData: this.fetchInvoiceData,
        url: this.returnUrl ? this.returnUrl : window.location.search.replace(/\?returnurl=/ig, '') + window.location.hash })
        .then(({ data }) => {
          if (data.merge) {
            if (this.enableMergeCart) {
              this.publish('modal.show.merge-cart', { url: data.url, showClose: false })
            } else {
              this.replaceCurrentCart()
            }
          } else if (!data.isCorrectMarket) {
            this.publish('modal.show.generic',
              {
                showClose: false,
                clickToClose: false,
                text: this.tStatic('signInIncorrectMarketDescription'),
                closeHandler: () => { window.location = data.cancelUrl },
                buttons: [{
                  header: this.tStatic('signInIncorrectMarketHeader'),
                  text: this.tStatic('signInIncorrectMarketContinue'),
                  usePrimaryButton: true,
                  handler: () => { this.signInToMarket({ username: this.username, password: this.password, rememberMe: this.rememberMe }) } } ]
              }
            )
          } else {
            if (window.location.pathname + window.location.search + window.location.hash === data.url) {
              window.location.reload()
            } else {
              window.location = data.url
            }
          }
        })
    },
    signInToMarket(options) {
      httpService.post('auth/signin', {
        username: options.username,
        password: options.password,
        redirectToMarket: true,
        fetchInvoiceData: this.fetchInvoiceData
      }).then(({ data }) => {
        window.location = data.url
      })
    }
  }
}
</script>

<style lang="scss">
.sign-in-box > *,
.sign-in-box > form > * {
  margin-bottom: measure(half);
}

.sign-in-box__actions {
  display: flex;
  flex-wrap: wrap;
}

.sign-in-box__button {
  width: 100%;
  margin-bottom: measure(half);

  @include media(medium) {
    width: 15rem;
    margin-bottom: 0;
  }
}

.sign-in-box__checkbox {
  margin-left: 0;

  @include media(medium) {
    margin-left: measure(full);
  }
}

.sign-in-box__reset-password {
  @include body-smaller-semi-bold;
  color: color(text-blue);
  text-decoration: none;
  width: 100%;
  margin-top: measure(full);

  @include media(small) {
    @include body-semi-bold;
  }

  &:hover {
    text-decoration: underline;
  }
}
</style>
